import React from 'react';
import cn from 'classnames';

import CheckIcon from 'widget/shared/icons/CheckIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';

import { CheckboxType } from './types';
import styles from './Checkbox.module.scss';

export type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  type?: CheckboxType;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  id?: string;
  name?: string;
};

const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  type = CheckboxType.checkbox,
  disabled = false,
  children,
  className,
  id,
  name
}) => {
  const handleChange = React.useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [checked, disabled, onChange]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
        event.preventDefault();
        onChange(!checked);
      }
    },
    [checked, disabled, onChange]
  );

  const isCheckbox = type === CheckboxType.checkbox;
  const isRadio = type === CheckboxType.radio;

  return (
    <div
      className={cn(
        styles['checkbox'],
        disabled && styles['checkbox_disabled'],
        className
      )}
      onClick={handleChange}
      onKeyDown={handleKeyDown}
      role={isRadio ? 'radio' : 'checkbox'}
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      <div
        className={cn(
          styles['checkbox__box'],
          checked && styles['checkbox__box_checked'],
          isRadio && styles['checkbox__box_radio']
        )}
      >
        {isCheckbox && checked && (
          <CheckIcon
            size={IconSize.XXS}
            color={IconColor.Main}
            className={styles['checkbox__icon']}
          />
        )}
        {isRadio && checked && <div className={styles['checkbox__circle']} />}
      </div>
      {children && <div className={styles['checkbox__label']}>{children}</div>}
      <input
        type={isRadio ? 'radio' : 'checkbox'}
        checked={checked}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange={() => {}}
        disabled={disabled}
        id={id}
        name={name}
        className={styles['checkbox__input']}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
};

export default React.memo(Checkbox);
